import { useRatingsOverview } from 'hooks/reviews/useRatingsOverview';
import { useReviewedProducts } from 'hooks/reviews/useReviewedProducts';
import { useGlobals } from 'hooks/useGlobals';
import type { ReviewsCarousel } from 'types/cms/blocks';
import type { JunipProductOverview } from 'types/reviews';
import { aggregateProductRatings } from 'utils/reviews/aggregateProductRatings';
import { getRootProductSlug } from 'utils/reviews/getRootProductSlug';

export type ReviewsCarouselStats = Pick<
  JunipProductOverview,
  'ratingAverage' | 'ratingCount' | 'recommendedPercentage'
>;

export const useReviewsCarouselStats = (
  reviews: ReviewsCarousel['reviews'],
): { stats?: ReviewsCarouselStats; isMultiProduct: boolean } => {
  const slugs = [
    ...new Set(
      reviews
        .map((review) => review.product?.slug)
        .filter((slug): slug is string => !!slug),
    ),
  ];

  const { data: globals } = useGlobals();
  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter: slugs.length ? slugs : undefined,
  });
  const { data: overview } = useRatingsOverview();

  const rootSlugs = slugs.map((slug) =>
    getRootProductSlug(
      globals?.reviewsMainProductSlugsAllCategories ?? [],
      slug,
    ),
  );
  const isMultiProduct =
    reviews.some((review) => !review.product?.slug) ||
    rootSlugs.includes(undefined) ||
    new Set(rootSlugs).size > 1;

  const products = slugs.length
    ? (reviewedProducts ?? []).filter(
        (product) => product.ratingAverage != null && !!product.ratingCount,
      )
    : [];

  if (products.length) {
    const { ratingAverage, ratingCount, recommendedPercentage } =
      aggregateProductRatings(products);

    return {
      stats: {
        ratingAverage,
        ratingCount,
        recommendedPercentage: recommendedPercentage ?? null,
      },
      isMultiProduct,
    };
  }

  return { stats: overview, isMultiProduct: true };
};
